/**
 * pre250 — Headset-Telefoniestatus per WebHID (Zero-Install-Kopplung).
 *
 * Idee: Im KSC steuert das Softphone (Avaya, Teams, …) das Headset über den
 * USB-Standard "HID Telephony" (Usage Page 0x0B). Das Headset meldet dabei den
 * Hook-Zustand (abgehoben = im Gespräch). Diesen Zustand liest der Browser-Tab
 * direkt am Headset mit — ohne Installation, ohne Server-Komponente, rein lokal.
 * Bei Änderung wird `bamoa:phone-busy` dispatcht (vorhandener Mechanismus →
 * Socket `phone:busy` → Video-Routing).
 *
 * Grenzen (bewusst): Nur Richtung Telefon → Video. Eine ACD-Steuerung (AUX)
 * ist ohne AES nicht möglich — siehe Konzeptdokument Avaya.
 *
 * Browser: Chrome/Edge (WebHID). Getestete Praxis: Jabra/Poly/EPOS folgen der
 * HID-Telephony-Page; das Parsing ist generisch über den Report-Deskriptor.
 */


// Minimale WebHID-Typen (nicht in lib.dom enthalten; nur die genutzten Felder).
type HIDDevice = {
  opened: boolean; productName?: string; collections?: any[];
  open(): Promise<void>; close(): Promise<void>;
  addEventListener(t: string, l: any): void; removeEventListener(t: string, l: any): void;
};
type HIDInputReportEvent = { reportId: number; data: DataView };

const TELEPHONY_PAGE = 0x0b;
const LED_PAGE = 0x08;
const USAGE_HOOK_SWITCH = 0x20; // Telephony: Hook Switch (1 = off-hook / im Gespräch)
const USAGE_LED_OFF_HOOK = 0x17; // LED: Off-Hook (viele Headsets: Busylight rot)
const LS_KEY = 'bamoa.headset.enabled';
export const LS_BUSYLIGHT = 'bamoa.headset.busylight';   // '1' = Busylight bei Videocall
export const LS_FAC_AUX = 'bamoa.telephony.fac.aux';     // Wähl-Code bei Videoannahme
export const LS_FAC_READY = 'bamoa.telephony.fac.ready'; // Wähl-Code bei Videoende

export interface HeadsetState {
  supported: boolean;
  connected: boolean;
  deviceName?: string;
  offHook: boolean;
}

type Listener = (s: HeadsetState) => void;

let device: HIDDevice | null = null;
let hookBit: { reportId: number; bitIndex: number } | null = null;
let busyLed: { reportId: number; bitIndex: number; byteLen: number } | null = null;
let state: HeadsetState = { supported: typeof navigator !== 'undefined' && 'hid' in navigator, connected: false, offHook: false };
const listeners = new Set<Listener>();
let lastDispatchedBusy: boolean | null = null;

function emit() {
  for (const l of listeners) { try { l({ ...state }); } catch { /* */ } }
}

function dispatchBusy(busy: boolean) {
  if (lastDispatchedBusy === busy) return;
  lastDispatchedBusy = busy;
  window.dispatchEvent(new CustomEvent('bamoa:phone-busy', { detail: { busy } }));
}

/** Sucht im Report-Deskriptor die Bit-Position des Hook-Switch-Inputs. */
function findHookBit(dev: HIDDevice): { reportId: number; bitIndex: number } | null {
  for (const col of dev.collections ?? []) {
    const stack = [col];
    while (stack.length) {
      const c = stack.pop()!;
      for (const child of c.children ?? []) stack.push(child);
      for (const rep of c.inputReports ?? []) {
        let bit = 0;
        for (const item of rep.items ?? []) {
          const size = (item.reportSize ?? 0) * (item.reportCount ?? 0);
          const usages: number[] = (item as any).usages ?? [];
          for (let i = 0; i < (item.reportCount ?? 0); i++) {
            const usage = usages[Math.min(i, usages.length - 1)];
            if (usage !== undefined) {
              const page = usage >>> 16, id = usage & 0xffff;
              if (page === TELEPHONY_PAGE && id === USAGE_HOOK_SWITCH) {
                return { reportId: rep.reportId ?? 0, bitIndex: bit + i * (item.reportSize ?? 1) };
              }
            }
          }
          bit += size;
        }
      }
    }
  }
  return null;
}

/** Sucht im Report-Deskriptor die Off-Hook-LED im OUTPUT (für das Busylight). */
function findBusyLed(dev: HIDDevice): { reportId: number; bitIndex: number; byteLen: number } | null {
  for (const col of dev.collections ?? []) {
    const stack = [col];
    while (stack.length) {
      const c = stack.pop()!;
      for (const child of (c as any).children ?? []) stack.push(child);
      for (const rep of (c as any).outputReports ?? []) {
        let bit = 0; let total = 0;
        for (const item of rep.items ?? []) total += (item.reportSize ?? 0) * (item.reportCount ?? 0);
        for (const item of rep.items ?? []) {
          const usages: number[] = (item as any).usages ?? [];
          for (let i = 0; i < (item.reportCount ?? 0); i++) {
            const usage = usages[Math.min(i, usages.length - 1)];
            if (usage !== undefined) {
              const page = usage >>> 16, id = usage & 0xffff;
              if (page === LED_PAGE && id === USAGE_LED_OFF_HOOK) {
                return { reportId: rep.reportId ?? 0, bitIndex: bit + i * (item.reportSize ?? 1), byteLen: Math.ceil(total / 8) };
              }
            }
          }
          bit += (item.reportSize ?? 0) * (item.reportCount ?? 0);
        }
      }
    }
  }
  return null;
}

/** Ist das (optionale) Busylight bei diesem Headset ansteuerbar? */
export function busylightAvailable(): boolean { return !!busyLed && !!device; }

/** Busylight (Off-Hook-LED) setzen — genutzt beim Start/Ende einer Videoberatung. */
export async function setBusylight(on: boolean): Promise<void> {
  if (!device || !busyLed) return;
  try {
    const buf = new Uint8Array(busyLed.byteLen || 1);
    if (on) buf[Math.floor(busyLed.bitIndex / 8)] |= 1 << (busyLed.bitIndex % 8);
    await (device as any).sendReport(busyLed.reportId, buf);
  } catch (e) { console.warn('[headset] Busylight', e); }
}

function onInputReport(e: HIDInputReportEvent) {
  if (!hookBit || e.reportId !== hookBit.reportId) return;
  const byte = e.data.getUint8(Math.floor(hookBit.bitIndex / 8));
  const offHook = ((byte >> (hookBit.bitIndex % 8)) & 1) === 1;
  if (offHook !== state.offHook) {
    state = { ...state, offHook };
    emit();
    dispatchBusy(offHook);
  }
}

async function attach(dev: HIDDevice): Promise<boolean> {
  try {
    if (!dev.opened) await dev.open();
  } catch (e) { console.warn('[headset] open fehlgeschlagen', e); return false; }
  const hb = findHookBit(dev);
  if (!hb) { console.warn('[headset] Kein Hook-Switch im Report-Deskriptor gefunden:', dev.productName); return false; }
  device = dev; hookBit = hb;
  busyLed = findBusyLed(dev);
  if (busyLed) console.log(`[headset] Busylight steuerbar (reportId=${busyLed.reportId}, bit=${busyLed.bitIndex})`);
  dev.addEventListener('inputreport', onInputReport as any);
  state = { ...state, connected: true, deviceName: dev.productName || 'Headset', offHook: false };
  emit();
  console.log(`[headset] gekoppelt: ${dev.productName} (reportId=${hb.reportId}, bit=${hb.bitIndex})`);
  return true;
}

export function headsetState(): HeadsetState { return { ...state }; }
export function onHeadsetChange(l: Listener): () => void { listeners.add(l); return () => listeners.delete(l); }

/** Vom Benutzer ausgelöste Kopplung (Browser-Dialog, einmalig pro Arbeitsplatz). */
export async function connectHeadset(): Promise<boolean> {
  if (!state.supported) return false;
  try {
    const devs = await (navigator as any).hid.requestDevice({
      filters: [{ usagePage: TELEPHONY_PAGE }],
    });
    for (const d of devs as HIDDevice[]) {
      if (await attach(d)) { localStorage.setItem(LS_KEY, '1'); return true; }
    }
  } catch (e) { console.warn('[headset] requestDevice', e); }
  return false;
}

export async function disconnectHeadset(): Promise<void> {
  localStorage.removeItem(LS_KEY);
  if (device) {
    try { device.removeEventListener('inputreport', onInputReport as any); } catch { /* */ }
    try { await device.close(); } catch { /* */ }
  }
  device = null; hookBit = null; busyLed = null;
  if (state.offHook) dispatchBusy(false);
  state = { ...state, connected: false, deviceName: undefined, offHook: false };
  emit();
}

/** Beim App-Start: bereits freigegebene Geräte automatisch wieder koppeln. */
export async function autoReconnectHeadset(): Promise<void> {
  if (!state.supported || localStorage.getItem(LS_KEY) !== '1') return;
  try {
    const devs: HIDDevice[] = await (navigator as any).hid.getDevices();
    for (const d of devs) {
      const hasTel = (d.collections ?? []).some((c: any) => c.usagePage === TELEPHONY_PAGE || (c.children ?? []).length);
      if (hasTel && await attach(d)) return;
    }
  } catch (e) { console.warn('[headset] autoReconnect', e); }
}

/**
 * Feature-Access-Code am Softphone wählen (Zero-Install-Rückkanal).
 * Öffnet einen tel:-Link — das als Standard-Telefonie-App registrierte Softphone
 * (z. B. Avaya Workplace) wählt den Code; in Avaya-Anlagen schalten Codes wie
 * "Aux Work" den Agenten aus der ACD-Verteilung. Codes konfiguriert der Benutzer
 * in den Einstellungen (Telefonie-Kopplung).
 */
export function dialFeatureCode(kind: 'aux' | 'ready'): boolean {
  const code = (localStorage.getItem(kind === 'aux' ? LS_FAC_AUX : LS_FAC_READY) || '').trim();
  if (!code || !/^[0-9*#+]{1,16}$/.test(code)) return false;
  try {
    const a = document.createElement('a');
    a.href = 'tel:' + encodeURIComponent(code);
    a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
    console.log(`[telephony] FAC gewählt (${kind}): ${code}`);
    return true;
  } catch { return false; }
}
